import { useState, useRef, useEffect, useCallback } from 'react';
import '../App.css';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  CUSTOM_SIZE_OPTION,
  customSizeOptions,
  parseCustomDims,
  getImageSizeInches,
  getPrintAreaInches,
  nearestTierForPricing as computeNearestPrintTier,
  computePreviewCanvasSize,
  getCustomSizeMetaString
} from './customPrintSize';

const STRETCH_TIERS = [
  { id: 'small', label: 'Small (up to 32" x 40")', price: 7.95 },
  { id: 'medium', label: 'Medium (up to 40" x 60")', price: 9.45 },
  { id: 'large', label: 'Large (up to 48" x 96")', price: 10.75 }
];

const getStretchValue = (stretchType) => {
  switch (stretchType) {
    case 'small':
      return 1.02;
    case 'medium':
      return 1.05;
    case 'large':
      return 1.08;
    default:
      return 1;
  }
};

const getEngineStretchType = (stretchType) => {
  switch (stretchType) {
    case 'small':
    case 'medium':
      return 'regular';
    case 'large':
      return 'gallery';
    default:
      return undefined;
  }
};

/** Minimum stretch tier that fits artwork (matches product labels). */
const minStretchTierForDims = (wIn, hIn) => {
  const L = Math.max(wIn, hIn);
  const S = Math.min(wIn, hIn);
  if (L <= 40 && S <= 32) return 'small';
  if (L <= 60 && S <= 40) return 'medium';
  return 'large';
};



function CanvasPrintandStretch({ image, originalImage, onEditImage }) {
  const userImage = image || localStorage.getItem('opticon_last_image');
  const uploadedUserImage = originalImage || userImage;
  const canvasRef = useRef(null);
  const isDragging = useRef(false);
  const lastPos = useRef({ x: 0, y: 0 });

  /* ===== STATES ===== */
  const printType = 'canvas';
  const [sideType, setSideType] = useState('mirror');
  const [loading, setLoading] = useState(false);
  const [, setProgress] = useState(0);
  const [imageSizeCm, setImageSizeCm] = useState(null);
  const [customSize, setCustomSize] = useState(CUSTOM_SIZE_OPTION);
  const [customWidthCm, setCustomWidthCm] = useState('');
  const [customHeightCm, setCustomHeightCm] = useState('');
  const [appliedCustomCropKey, setAppliedCustomCropKey] = useState('');
  const [imgPos, setImgPos] = useState({ x: 0, y: 0 });
  const DPI = 300;

  const galleryOptions = [
    { id: 'mirror', label: 'Image Mirrored' },
    { id: 'black', label: 'Printed Black' },
    { id: 'white', label: 'Printed White' }
  ];

  const { widthIn, heightIn, normalizedSize } = getImageSizeInches(imageSizeCm);
  const cmToInches = (cm) => {
    const parsed = parseFloat(cm);
    if (!parsed || parsed <= 0) return 0;

    return parsed / 2.54;
  };
  const customDimsParsed = () => {
    const widthInches = cmToInches(customWidthCm);
    const heightInches = cmToInches(customHeightCm);

    return parseCustomDims(widthInches, heightInches);
  };
  const isCustomWhMode = customSize === CUSTOM_SIZE_OPTION;
  const customDims = isCustomWhMode ? customDimsParsed() : null;
  const customWidthValue = Number(customWidthCm);
  const customHeightValue = Number(customHeightCm);
  const hasValidCustomSize =
    Number.isFinite(customWidthValue) &&
    Number.isFinite(customHeightValue) &&
    customWidthValue > 0 &&
    customHeightValue > 0;
  const customCropKey = hasValidCustomSize
    ? `${customWidthValue}x${customHeightValue}`
    : '';
  const showApplyCustomCrop =
    isCustomWhMode && hasValidCustomSize && customCropKey !== appliedCustomCropKey;

  const handleApplyCustomCrop = () => {
    if (!showApplyCustomCrop || !onEditImage) return;

    setAppliedCustomCropKey(customCropKey);
    onEditImage({
      aspect: customWidthValue / customHeightValue,
      customWidthCm: customWidthValue,
      customHeightCm: customHeightValue
    });
  };
  const area = getPrintAreaInches(customSize, customDims, widthIn, heightIn);
  const stretchType = minStretchTierForDims(area.w, area.h);
  const { previewCanvasW, previewCanvasH } = computePreviewCanvasSize(area);

  const nearestTier = computeNearestPrintTier({
    printType,
    customSize,
    customDims,
    widthIn,
    heightIn
  });

  const requiredStretchTier = minStretchTierForDims(area.w, area.h);
  const effectiveStretchForPrice = requiredStretchTier;

  /* ===== PRICES ===== */
  const selectedStretchTier = STRETCH_TIERS.find(
    (tier) => tier.id === effectiveStretchForPrice
  );

  /* ===== TOTAL PRICE ===== */
  const totalPrice = selectedStretchTier?.price || 0;

  /* ===== GST (10%) ===== */
  const gstAmount = totalPrice * 0.10;
  const totalWithGST = totalPrice + gstAmount;

  const handleMouseDown = (e) => {
    if (!stretchType) return;
    isDragging.current = true;
    lastPos.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current) return;

    const dx = e.clientX - lastPos.current.x;
    const dy = e.clientY - lastPos.current.y;

    setImgPos((prev) => {
      const canvas = canvasRef.current;
      if (!canvas) return prev;

      const stretchValue = getStretchValue(stretchType);
      const limitX = (canvas.width * (stretchValue - 1)) / 2;
      const limitY = (canvas.height * (stretchValue - 1)) / 2;

      const x = Math.max(-limitX, Math.min(limitX, prev.x + dx));
      const y = Math.max(-limitY, Math.min(limitY, prev.y + dy));

      return { x, y };
    });

    lastPos.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  /* ============================================================
   FLAT CANVAS PREVIEW
   Canvas Print & Stretch

   - Renders the image stretched to the selected canvas tier
   - Keeps the image centered
   - Allows the user to drag the image
   - Does NOT apply picture-frame moulding
============================================================ */

  const renderRealCanvas = useCallback(async () => {
    if (!userImage || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    /* ========================================================
       LOAD USER IMAGE
    ======================================================== */

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = userImage;

    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
    });


    /* ========================================================
       CANVAS DIMENSIONS
    ======================================================== */

    const W = canvas.width;
    const H = canvas.height;


    /* ========================================================
       CLEAR CANVAS
    ======================================================== */

    ctx.clearRect(0, 0, W, H);


    /* ========================================================
       STRETCH VALUE
    ======================================================== */

    const stretchValue = getStretchValue(stretchType);


    /* ========================================================
       CALCULATE STRETCHED IMAGE SIZE
    ======================================================== */

    const drawW = W * stretchValue;
    const drawH = H * stretchValue;


    /* ========================================================
       CENTER IMAGE
  
       imgPos.x / imgPos.y allows the user
       to move the image around the canvas.
    ======================================================== */

    const drawX =
      -(drawW - W) / 2 +
      imgPos.x;

    const drawY =
      -(drawH - H) / 2 +
      imgPos.y;


    /* ========================================================
       CLIP TO CANVAS
  
       Anything outside the canvas area is hidden.
    ======================================================== */

    ctx.save();

    ctx.beginPath();

    ctx.rect(
      0,
      0,
      W,
      H
    );

    ctx.clip();


    /* ========================================================
       DRAW IMAGE
    ======================================================== */

    ctx.drawImage(
      img,
      drawX,
      drawY,
      drawW,
      drawH
    );


    /* ========================================================
       RESTORE CANVAS
    ======================================================== */

    ctx.restore();

  }, [
    imgPos,
    stretchType,
    userImage,
    previewCanvasW,
    previewCanvasH
  ]);


  /* ============================================================
     RE-RENDER PREVIEW WHEN IMAGE OR POSITION CHANGES
  ============================================================ */

  useEffect(() => {
    renderRealCanvas();
  }, [renderRealCanvas]);

  useEffect(() => {
    renderRealCanvas();
  }, [renderRealCanvas]);

  /* ================= EXPORT EDITED IMAGE ================= */
  const exportEditedImage = () => {
    const canvas = canvasRef.current;
    return canvas.toDataURL('image/png');
  };

  /* ================= ADD TO CART ================= */
  const handleAddToCart = async () => {
    if (!canvasRef.current) {
      alert('Image not ready.');
      return;
    }

    if (isCustomWhMode && !hasValidCustomSize) {
      Swal.fire({
        icon: 'warning',
        title: 'Print Size Required',
        text: 'Please enter the print size width and height in cm.'
      });
      return;
    }

    setLoading(true);
    setProgress(0);

    try {
      /* ================================
         IMAGE TO BASE64 (REAL PIPELINE)
      ================================ */

      const imageDataUrl = exportEditedImage();

      // Convert base64 to blob.
      const resImg = await fetch(imageDataUrl);
      const blob = await resImg.blob();

      // Convert blob to clean base64.
      const editedImageBase64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });

      /* ================================
         PAYLOAD
      ================================ */

      const payload = {
        id: 8431,
        quantity: 1,
        meta_data: [
          { key: 'print_type', value: printType },

          {
            key: 'image_size',
            value: `${imageSizeCm.widthCm} × ${imageSizeCm.heightCm} cm`
          },

          {
            key: 'custom_size',
            value: getCustomSizeMetaString(
              normalizedSize,
              customSize,
              customDimsParsed()
            )
          },

          {
            key: 'print_size',
            value: nearestTier.key
          },

          // Keep for backward compatibility if used elsewhere
          {
            key: 'print_nearest_tier',
            value: nearestTier.key
          },

          {
            key: 'stretch_type',
            value: getEngineStretchType(effectiveStretchForPrice) || 'no-stretch'
          },

          {
            key: 'stretch_value',
            value: effectiveStretchForPrice
          },

          {
            key: 'stretch_size_tier',
            value: effectiveStretchForPrice
          },

          {
            key: 'stretch_price',
            value: totalPrice
          },

          {
            key: 'side_type',
            value: sideType
          },

          {
            key: 'total_price',
            value: totalWithGST
          },

          {
            key: 'edited_image',
            value: editedImageBase64
          },
          {
            key: 'user-uploaded-image',
            value: uploadedUserImage
          }
        ]
      };

      console.log('ADD TO CART PAYLOAD:', payload);

      /* ================================
         API CALL
      ================================ */

      const response = await axios.post(
        'https://opticonframing.com.au/wp-json/opticon/v1/canvas-print-and-stretch/add-to-cart',
        payload,
        {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true,
          onUploadProgress: (e) => {
            if (!e.total) return;
            const percent = Math.round((e.loaded * 100) / e.total);
            setProgress(percent);
          }
        }
      );

      /* ================================
         RESPONSE
      ================================ */

      if (response.data?.success) {
        setProgress(100);

        setTimeout(() => {
          window.location.href = 'https://opticonframing.com.au/cart';
        }, 700);
      } else {
        throw new Error(response.data?.error || 'Server error');
      }
    } catch (err) {
      console.error('Add to cart error:', err);
      alert('Failed to add to cart. Check console for details.');
      setLoading(false);
      setProgress(0);
    }
  };

  useEffect(() => {
    if (!userImage) return;

    const img = new Image();
    img.onload = () => {
      const widthCm = ((img.width / DPI) * 2.54).toFixed(1);
      const heightCm = ((img.height / DPI) * 2.54).toFixed(1);

      setImageSizeCm({
        widthCm,
        heightCm,
        widthPx: img.width,
        heightPx: img.height
      });
    };

    img.src = userImage;
  }, [userImage]);

  return (
    <div className="product-page">
      {loading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0,0,0,0.8)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}>
          {/* Circular Loader */}
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            border: '5px solid rgba(255,255,255,0.1)',
            borderTop: '5px solid #1E5FFF',
            animation: 'spin 1s linear infinite',
            marginBottom: '20px'
          }}></div>

          {/* Percentage Text */}
          <p style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>
            Loading...
          </p>

          <style>{`
            @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
          `}</style>
        </div>
      )}

      {/* LEFT PREVIEW */}
      <div className="product-image-panel">
        <canvas
          ref={canvasRef}
          width={previewCanvasW}
          height={previewCanvasH}
          className="real-canvas-preview"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        />
      </div>

      {/* RIGHT PANEL */}
      <div className="product-info-panel">
        <h1 className="product-title">Canvas Print & Stretch</h1>

        {/* Image Size Info */}
        {imageSizeCm && (
          <div className="cpsimage-size-row">
            <span className="form-label">Image Size</span>
            <span className="cpsimage-size-value">
              {imageSizeCm.widthCm} x {imageSizeCm.heightCm} cm
            </span>

            <button
              type="button"
              className="edit-image-btn"
              onClick={() => onEditImage?.()}
              title="Edit image"
            >
              <i className="fa-solid fa-pen-to-square"></i>
            </button>
          </div>
        )}

        {/* Custom Size */}
        <div className="print-option-row">
          <label className="form-label">Custom Size</label>

          <select
            className="form-select"
            value={customSize}
            onChange={(e) => setCustomSize(e.target.value)}
          >
            <option value={CUSTOM_SIZE_OPTION}>
              Custom width × height (cm)
            </option>

            <option value="">
              Auto (Based on image size)
            </option>


            {customSizeOptions.map((opt) => (
              <option key={opt.label} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {isCustomWhMode && (
          <div className="print-size-canvas-block">
            <label className="form-label print-size-canvas-block__title">
              Print Size
            </label>

            <div className="print-size-canvas-block__row">

              <div className="print-size-canvas-block__field">
                <span
                  className="print-size-canvas-block__dim-label"
                  id="print-canvas-w-label"
                >
                  W
                </span>

                <input
                  id="print-canvas-w-cm"
                  type="number"
                  className="print-size-canvas-block__input"
                  min={1}
                  step={0.1}
                  placeholder="cm"
                  inputMode="decimal"
                  autoComplete="off"
                  aria-labelledby="print-canvas-w-label"
                  value={customWidthCm}
                  onChange={(e) => setCustomWidthCm(e.target.value)}
                />
              </div>

              <div className="print-size-canvas-block__field">
                <span
                  className="print-size-canvas-block__dim-label"
                  id="print-canvas-h-label"
                >
                  H
                </span>

                <input
                  id="print-canvas-h-cm"
                  type="number"
                  className="print-size-canvas-block__input"
                  min={1}
                  step={0.1}
                  placeholder="cm"
                  inputMode="decimal"
                  autoComplete="off"
                  aria-labelledby="print-canvas-h-label"
                  value={customHeightCm}
                  onChange={(e) => setCustomHeightCm(e.target.value)}
                />
              </div>

            </div>

            {showApplyCustomCrop && (
              <button
                type="button"
                className="print-size-canvas-block__apply"
                onClick={handleApplyCustomCrop}
              >
                Apply
              </button>
            )}
          </div>
        )}

        {/* Gallery */}
        <div className="print-option-row">
          <label className="form-label">Gallery Wrap</label>
          <select
            className="form-select"
            value={sideType}
            onChange={e => setSideType(e.target.value)}
          >
            {galleryOptions.map(opt => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="print-description">
          A gallery image wrap on canvas is a technique where the printed or painted artwork stretches entirely over the edges of a thick wooden frame and is stapled to the back.
        </div>

        {/* Price */}
        <div className="price-cart-row">
          <div className="product-price">
            ${totalPrice.toFixed(2)} + GST (10%) ${gstAmount.toFixed(2)} = ${totalWithGST.toFixed(2)}
          </div>

          <button
            className="add-to-cart-btn"
            onClick={handleAddToCart}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Add to Cart'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CanvasPrintandStretch;
